import type { JSX } from 'react';
import { cn } from '@/lib/utils';
import type { CatalogEntry } from './catalog';

/** Step one of "Add source" (design L608–616): eight types, the unbuilt ones greyed out. */
export function CatalogGrid({
  entries,
  onPick,
}: {
  entries: readonly CatalogEntry[];
  onPick: (entry: CatalogEntry) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3 overflow-y-auto px-7 py-6">
      {entries.map((entry) => {
        const usable = entry.state === 'available';
        return (
          <button
            key={entry.id}
            type="button"
            disabled={!usable}
            title={entry.reason}
            onClick={() => onPick(entry)}
            className={cn(
              'bg-bg border-line flex items-start gap-3.5 rounded-[14px] border p-4 text-left text-inherit transition-[border-color,transform] duration-200',
              usable ? 'hover:border-acc hover:-translate-y-0.5' : 'cursor-not-allowed opacity-55',
            )}
          >
            <span className="bg-surf2 border-line grid size-10 flex-none place-items-center rounded-[10px] border font-mono text-[11px] font-medium">
              {entry.glyph}
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-2 text-sm font-semibold">
                {entry.name}
                {!usable && <Badge state={entry.state} />}
              </span>
              <span className="text-mute mt-[3px] block text-[12.5px] leading-[1.45]">
                {entry.state === 'unavailable' ? entry.reason : entry.short}
              </span>
              {entry.state !== 'coming-soon' && (
                <span className="text-acc mt-2 block font-mono text-[10.5px]">{entry.auth}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Badge({ state }: { state: CatalogEntry['state'] }): JSX.Element {
  return (
    <span className="bg-surf2 border-line text-mute rounded-full border px-2 py-px font-mono text-[9.5px] font-normal tracking-[.08em]">
      {state === 'coming-soon' ? 'COMING SOON' : 'UNAVAILABLE'}
    </span>
  );
}
